import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { combineLatest, ReplaySubject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { BadgeConfirmationPage } from '../../../../modals/badge-confirmation/badge-confirmation.page';
import { Badge } from '../../../../../models/badge';
import { BadgesService } from '../../../../../services/badges.service';
import { UserService } from '../../../../../services/user.service';

interface UserViewModel {
  userId: string;
  fullName: string;
  badges: Array<Badge & { selected: boolean }>;
}

@Component({
  selector: 'app-user',
  templateUrl: './user.page.html',
  styleUrls: ['./user.page.scss'],
})
export class UserPage implements OnInit, OnDestroy {

  viewModel: UserViewModel;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  get hasItemsSelected() {
    return !this.viewModel || !this.viewModel.badges.some(badge => badge.selected);
  }

  constructor(
    public modalController: ModalController,
    private badgesService: BadgesService,
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit() {
    const userId = this.route.snapshot.paramMap.get('userId');

    combineLatest([
      this.badgesService.getBadges(),
      this.userService.getUserById(userId, { invalidateCache: true })
    ]).pipe(
      takeUntil(this.destroyed$),
      map(([badges, user]) => ({
          userId,
          fullName: user.fullName,
          badges: badges.filter(badge => !(Object.keys(user.badges) || []).includes(badge.id)).map(badge => ({
            ...badge,
            selected: false
          }))
        }))
    ).subscribe(viewModel => {
      this.viewModel = viewModel;
    });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  mockLoadingItems(count: number) {
    return new Array(count);
  }

  async presentBadgeConfirmationModal() {
    const { userId, fullName: user, badges } = this.viewModel;

    const selectedBadges = badges.filter(badge => badge.selected);

    const modal = await this.modalController.create({
      component: BadgeConfirmationPage,
      cssClass: 'app-auto-height-modal',
      componentProps: {
        user,
        badges: selectedBadges.map(({ iconColored: icon, name }) => ({
          icon,
          name
        }))
      }
    });

    await modal.present();

    const { data: confirmed } = await modal.onWillDismiss();
    if (confirmed) {
      this.badgesService.assignBadges(userId, selectedBadges.map(badge => badge.id))
        .subscribe(() => {
          this.router.navigate(['..'], { relativeTo: this.route });
        });
    }
  }
}
